/**
 * ipGeoService.js
 * Fetches real-world IP geolocation data from multiple free public APIs.
 * Prioritizes HTTPS-safe/CORS-enabled APIs.
 * Results are cached in sessionStorage to avoid redundant calls.
 */

const CACHE_PREFIX = "ipgeo_";

const getCached = (ip) => {
    try {
        const raw = sessionStorage.getItem(CACHE_PREFIX + ip);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const setCache = (ip, data) => {
    try {
        sessionStorage.setItem(CACHE_PREFIX + ip, JSON.stringify(data));
    } catch {
        // sessionStorage might be full — silently ignore
    }
};

/**
 * Normalise response from freeipapi.com
 * Free: 60 req/min, HTTPS, CORS SUPPORTED
 */
const tryFreeIpApi = async (ip) => {
    const res = await fetch(`https://freeipapi.com/api/json/${ip}`, {
        signal: AbortSignal.timeout(5000),
    });
    const data = await res.json();
    if (!data.ipAddress) throw new Error("freeipapi failed");
    return {
        ip,
        country: data.countryName,
        countryCode: data.countryCode,
        region: data.regionName,
        city: data.cityName,
        lat: data.latitude,
        lon: data.longitude,
        timezone: data.timeZone,
        isp: null,
        org: null,
        asn: null,
        mobile: data.isProxy === false ? false : null,
        proxy: data.isProxy,
        hosting: null,
        source: "freeipapi.com",
        raw: data
    };
};

/**
 * Normalise response from ipapi.co
 * Free: 1,000 req/day, HTTPS, CORS SUPPORTED
 */
const tryIpApiCo = async (ip) => {
    const res = await fetch(`https://ipapi.co/${ip}/json/`, {
        signal: AbortSignal.timeout(5000),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.reason || "ipapi.co failed");
    return {
        ip,
        country: data.country_name,
        countryCode: data.country_code,
        region: data.region,
        city: data.city,
        lat: data.latitude,
        lon: data.longitude,
        timezone: data.timezone,
        isp: data.org,
        org: data.org,
        asn: data.asn,
        mobile: null,
        proxy: null,
        hosting: null,
        source: "ipapi.co",
        raw: data
    };
};

/**
 * Normalise response from db-ip.com
 * Free: no key needed for basic info, HTTPS/CORS SUPPORTED
 */
const tryDbIp = async (ip) => {
    const res = await fetch(`https://api.db-ip.com/v2/free/${ip}`, {
        signal: AbortSignal.timeout(5000),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error || "db-ip failed");
    return {
        ip,
        country: data.countryName,
        countryCode: data.countryCode,
        region: data.stateProv,
        city: data.city,
        lat: null,
        lon: null,
        timezone: null,
        isp: null,
        org: null,
        asn: null,
        mobile: null,
        proxy: null,
        hosting: null,
        source: "db-ip.com",
    };
};

/**
 * Normalise response from ip-api.com
 * Free: 1,000 req/min. 
 * NOTE: HTTPS requires a key. This only works on HTTP sites.
 */
const tryIpApi = async (ip) => {
    // Only attempt if we are on http or if we want to risk it (some browsers block it on https)
    const isHttps = window.location.protocol === "https:";
    if (isHttps) throw new Error("ip-api.com requires PRO key for HTTPS");

    const res = await fetch(
        `http://ip-api.com/json/${ip}?fields=status,message,country,countryCode,region,regionName,city,lat,lon,timezone,isp,org,as,mobile,proxy,hosting`,
        { signal: AbortSignal.timeout(5000) }
    );
    const data = await res.json();
    if (data.status !== "success") throw new Error(data.message || "ip-api failed");
    return {
        ip,
        country: data.country,
        countryCode: data.countryCode,
        region: data.regionName,
        city: data.city,
        lat: data.lat,
        lon: data.lon,
        timezone: data.timezone,
        isp: data.isp,
        org: data.org,
        asn: data.as,
        mobile: data.mobile,
        proxy: data.proxy,
        hosting: data.hosting,
        source: "ip-api.com",
    };
};

const isPrivateIP = (ip) => {
    if (!ip) return false;
    // Basic check for private ranges
    // 127.x.x.x
    if (ip.startsWith("127.")) return true;
    // 10.x.x.x
    if (ip.startsWith("10.")) return true;
    // 192.168.x.x
    if (ip.startsWith("192.168.")) return true;
    // 172.16.x.x to 172.31.x.x
    if (ip.startsWith("172.")) {
        const parts = ip.split(".");
        if (parts.length > 1) {
            const secondPart = parseInt(parts[1]);
            if (secondPart >= 16 && secondPart <= 31) return true;
        }
    }
    // localhost
    if (ip.toLowerCase() === "localhost") return true;
    // IPv6 loopback
    if (ip === "::1") return true;
    return false;
};

/**
 * Main export — fetch geo info for an IP, with fallback chain + cache
 * @param {string} ip
 * @returns {Promise<Object>} normalised geo info object
 */
export const fetchIpGeoInfo = async (ip) => {
    if (!ip) return null;

    // Return from cache if available
    const cached = getCached(ip);
    if (cached) return cached;

    // Handle private IPs early
    if (isPrivateIP(ip)) {
        return {
            ip,
            country: "Local Network",
            countryCode: "LOCAL",
            region: "LAN",
            city: "Private IP",
            isp: "Internal Network",
            source: "local-detect"
        };
    }

    // Prioritize HTTPS-friendly APIs & remove problematic CORS ones
    const apis = [tryFreeIpApi, tryIpApiCo, tryDbIp, tryIpApi];

    for (const api of apis) {
        try {
            const result = await api(ip);
            setCache(ip, result);
            return result;
        } catch (err) {
            console.warn(`IP Geo API failed (${api.name}):`, err.message);
            // Continue to next API
        }
    }

    // All APIs exhausted
    throw new Error("Geolocation lookup failed (CORS or Private IP). Please try again later.");
};
